import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  TextField,
  InputAdornment,
  Skeleton,
} from '@mui/material';

import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaptopWindowsIcon from '@mui/icons-material/LaptopWindows';
import DnsIcon from '@mui/icons-material/Dns';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CampaignIcon from '@mui/icons-material/Campaign';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { apiServer } from '../../api';
import { useLogin } from '../../contexts/LoginContext';
import { IDispositivo, IClienteUnidadeDisco } from '../../types/dispositivos';

interface IInfoUltimaVersao {
  versao: string;
  obrigatorio: boolean;
  novidades: string;
  dataPublicacao?: string;
  urlDownload?: string;
}

interface IStatusComandoResposta {
  id: number;
  clienteID: number;
  tipo: string;
  situacao: string;
  saida: string;
  executadoComSucesso: boolean;
  dataCadastro: string;
  dataExecucao?: string;
}

function formatarTempoRelativo(dataIso?: string | null): string {
  if (!dataIso) return 'Nunca visto';
  const data = new Date(dataIso);
  if (isNaN(data.getTime())) return '—';

  const agora = new Date();
  const diffSegundos = Math.floor((agora.getTime() - data.getTime()) / 1000);

  if (diffSegundos < 60) return 'Agora mesmo';
  if (diffSegundos < 3600) {
    const min = Math.floor(diffSegundos / 60);
    return `há ${min} ${min === 1 ? 'minuto' : 'minutos'}`;
  }
  if (diffSegundos < 86400) {
    const horas = Math.floor(diffSegundos / 3600);
    return `há ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  }
  const dias = Math.floor(diffSegundos / 86400);
  if (dias < 30) return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;

  return data.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatarBytesParaGB(bytes?: number | string | null): string {
  if (!bytes) return '0 GB';
  const num = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
  if (isNaN(num) || num <= 0) return '0 GB';
  const gb = num / (1024 * 1024 * 1024);
  return `${gb.toFixed(1)} GB`;
}

function compararVersoes(v1?: string, v2?: string): number {
  if (!v1 || !v2) return 0;
  const p1 = v1.replace(/^v/i, '').split('.').map((n) => parseInt(n, 10) || 0);
  const p2 = v2.replace(/^v/i, '').split('.').map((n) => parseInt(n, 10) || 0);
  const maxLen = Math.max(p1.length, p2.length);
  for (let i = 0; i < maxLen; i++) {
    const num1 = p1[i] || 0;
    const num2 = p2[i] || 0;
    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }
  return 0;
}

export default function Dispositivos() {
  const theme = useTheme();
  const [login] = useLogin();
  const [dispositivos, setDispositivos] = useState<IDispositivo[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroTexto, setFiltroTexto] = useState<string>('');

  // Informações da versão mais recente no servidor
  const [infoVersao, setInfoVersao] = useState<IInfoUltimaVersao | null>(null);
  const [modalNovidadesAberto, setModalNovidadesAberto] = useState<boolean>(false);

  // Modal de confirmação de reinício
  const [modalReinicioAberto, setModalReinicioAberto] = useState<boolean>(false);
  const [dispositivoSelecionado, setDispositivoSelecionado] = useState<IDispositivo | null>(null);
  const [reiniciando, setReiniciando] = useState<boolean>(false);

  // Modal de atualização remota (AutoUpdate)
  const [modalAtualizacaoAberto, setModalAtualizacaoAberto] = useState<boolean>(false);
  const [atualizando, setAtualizando] = useState<boolean>(false);
  const [statusAtualizacaoTexto, setStatusAtualizacaoTexto] = useState<string>('');
  const [resultadoAtualizacao, setResultadoAtualizacao] = useState<{ sucesso: boolean; mensagem: string } | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Snackbar de feedback
  const [snackbarAberto, setSnackbarAberto] = useState<boolean>(false);
  const [snackbarMensagem, setSnackbarMensagem] = useState<string>('');
  const [snackbarSeveridade, setSnackbarSeveridade] = useState<'success' | 'info' | 'warning' | 'error'>('success');

  const clienteID = login.usuario?.usuarioCliente?.clienteID || (login.usuario as any)?.clienteID;

  // Limpeza de polling ao desmontar
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Consulta da versão mais recente do cliente
  const carregarVersaoMaisRecente = useCallback(() => {
    apiServer<IInfoUltimaVersao>()
      .get('v2/updates/check?platform=windows')
      .then((resposta) => {
        if (resposta.data && resposta.data.versao) {
          setInfoVersao(resposta.data);
        }
      })
      .catch((err) => {
        console.warn('Não foi possível verificar a versão mais recente do agente:', err);
      });
  }, []);

  const carregarDispositivos = useCallback(() => {
    if (!clienteID) {
      setCarregando(false);
      return;
    }

    setCarregando(true);
    setErro(null);

    apiServer<IDispositivo[]>()
      .get(`v2/dispositivos/${clienteID}`, login.accessToken)
      .then((resposta) => {
        const dados = Array.isArray(resposta.data) ? resposta.data : [];
        setDispositivos(dados);
      })
      .catch((err) => {
        console.error('Erro ao carregar dispositivos:', err);
        setErro('Não foi possível carregar os dados dos dispositivos. Verifique sua conexão e tente novamente.');
      })
      .finally(() => {
        setCarregando(false);
      });
  }, [clienteID, login.accessToken]);

  useEffect(() => {
    carregarDispositivos();
    carregarVersaoMaisRecente();
  }, [carregarDispositivos, carregarVersaoMaisRecente]);

  // Ações de Reinício
  const handleAbrirModalReinicio = (disp: IDispositivo) => {
    setDispositivoSelecionado(disp);
    setModalReinicioAberto(true);
  };

  const handleFecharModalReinicio = () => {
    if (reiniciando) return;
    setModalReinicioAberto(false);
    setDispositivoSelecionado(null);
  };

  const handleConfirmarReinicio = () => {
    if (!dispositivoSelecionado || !clienteID || !login.accessToken) return;

    setReiniciando(true);

    apiServer<{ sucesso: boolean; mensagem: string }>()
      .post(`v2/dispositivos/${dispositivoSelecionado.id}/reiniciar`, login.accessToken, { clienteID })
      .then((resposta) => {
        setSnackbarMensagem(resposta.data?.mensagem || 'Comando de reinicialização enviado com sucesso.');
        setSnackbarSeveridade('success');
        setSnackbarAberto(true);
        handleFecharModalReinicio();
        setTimeout(carregarDispositivos, 2500);
      })
      .catch((err) => {
        console.error('Erro ao enviar comando de reinício:', err);
        setSnackbarMensagem('Não foi possível despachar o comando de reinício. Tente novamente.');
        setSnackbarSeveridade('error');
        setSnackbarAberto(true);
      })
      .finally(() => {
        setReiniciando(false);
      });
  };

  // Ações de Atualização Remota (AutoUpdate)
  const handleAbrirModalAtualizacao = (disp: IDispositivo) => {
    setDispositivoSelecionado(disp);
    setResultadoAtualizacao(null);
    setAtualizando(false);
    setStatusAtualizacaoTexto('');
    setModalAtualizacaoAberto(true);
  };

  const handleFecharModalAtualizacao = () => {
    if (atualizando) return;
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setModalAtualizacaoAberto(false);
    setDispositivoSelecionado(null);
    setResultadoAtualizacao(null);
  };

  const iniciarPollingComandoAtualizacao = (comandoId: number | string) => {
    let tentativas = 0;
    const maxTentativas = 45; // até 90s (intervalo de 2s)

    setStatusAtualizacaoTexto('Comando publicado. Aguardando o agente sincronizar e iniciar a atualização...');

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      tentativas++;

      try {
        const resposta = await apiServer<IStatusComandoResposta>()
          .get(`v2/comando/status/${comandoId}`, login.accessToken);

        const cmd = resposta.data;

        if (cmd) {
          if (cmd.situacao === 'Sucesso') {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
            setAtualizando(false);
            setResultadoAtualizacao({
              sucesso: true,
              mensagem: cmd.saida || 'Atualização aplicada com sucesso no dispositivo! O agente foi reiniciado na nova versão.',
            });
            setSnackbarMensagem('Agente atualizado com sucesso!');
            setSnackbarSeveridade('success');
            setSnackbarAberto(true);
            carregarDispositivos();
          } else if (cmd.situacao === 'Erro') {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
            setAtualizando(false);
            setResultadoAtualizacao({
              sucesso: false,
              mensagem: cmd.saida || 'O agente reportou um erro durante a aplicação da atualização.',
            });
            setSnackbarMensagem('Falha ao atualizar agente no dispositivo.');
            setSnackbarSeveridade('error');
            setSnackbarAberto(true);
          } else {
            // Situação Pendente
            setStatusAtualizacaoTexto(
              `Aguardando sincronização do agente (${tentativas * 2}s decorridos)... O processo será baixado e validado em segundo plano.`
            );
          }
        }
      } catch (err) {
        console.warn('Erro ao consultar status do comando de atualização:', err);
      }

      if (tentativas >= maxTentativas) {
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        setAtualizando(false);
        setResultadoAtualizacao({
          sucesso: false,
          mensagem:
            'Tempo limite esgotado aguardando confirmação do agente. O comando permanece registrado na fila do servidor e será processado na próxima comunicação.',
        });
      }
    }, 2000);
  };

  const handleConfirmarAtualizacao = () => {
    if (!dispositivoSelecionado || !clienteID || !login.accessToken) return;

    setAtualizando(true);
    setResultadoAtualizacao(null);
    setStatusAtualizacaoTexto('Enviando comando de atualização para o servidor...');

    apiServer<{ sucesso: boolean; mensagem: string; comandoId: number }>()
      .post(`v2/dispositivos/${dispositivoSelecionado.id}/atualizar`, login.accessToken, {
        clienteID,
        versaoAlvo: infoVersao?.versao || '',
        forcar: false,
      })
      .then((resposta) => {
        if (resposta.data?.comandoId) {
          iniciarPollingComandoAtualizacao(resposta.data.comandoId);
        } else {
          setAtualizando(false);
          setResultadoAtualizacao({
            sucesso: false,
            mensagem: resposta.data?.mensagem || 'Não foi possível registrar o comando de atualização.',
          });
        }
      })
      .catch((err) => {
        console.error('Erro ao despachar comando de atualização:', err);
        setAtualizando(false);
        setResultadoAtualizacao({
          sucesso: false,
          mensagem: err?.response?.data?.mensagem || 'Erro de comunicação ao enviar comando de atualização.',
        });
      });
  };

  const copiarParaTransferencia = (texto?: string | null) => {
    if (!texto || texto === '—') return;
    navigator.clipboard.writeText(texto);
    setSnackbarMensagem(`Endereço copiado: ${texto}`);
    setSnackbarSeveridade('info');
    setSnackbarAberto(true);
  };

  // Cálculos de métricas defensivos
  const totalDispositivos = dispositivos.length;
  const onlineCount = dispositivos.filter((d) => d?.status === 'Online').length;
  const offlineCount = Math.max(0, totalDispositivos - onlineCount);

  // Versão disponível
  const versaoServidor = infoVersao?.versao;
  const versoesAgentes = Array.from(new Set(dispositivos.map((d) => d?.versaoAgente).filter(Boolean)));
  const versaoExibicao = versaoServidor || versoesAgentes[0] || '1.2.6';

  // Filtragem defensiva
  const dispositivosFiltrados = dispositivos.filter((d) => {
    if (!d) return false;
    const busca = filtroTexto.toLowerCase().trim();
    if (!busca) return true;
    const nome = (d.nomeDispositivo || '').toLowerCase();
    const so = (d.sistemaOperacional || '').toLowerCase();
    const ipL = (d.ipLocal || '').toLowerCase();
    const ipE = (d.ipExterno || '').toLowerCase();
    const ver = (d.versaoAgente || '').toLowerCase();

    return (
      nome.includes(busca) ||
      so.includes(busca) ||
      ipL.includes(busca) ||
      ipE.includes(busca) ||
      ver.includes(busca)
    );
  });

  return (
    <Box sx={{ pb: 6 }}>
      {/* Cabeçalho da Página */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.primary' }}>
            <DevicesIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
            Gerenciamento de Dispositivos & Agentes
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Monitoramento em tempo real, status de conectividade e controle remoto dos servidores e computadores sincronizados.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {infoVersao && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CampaignIcon />}
              onClick={() => setModalNovidadesAberto(true)}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
            >
              Novidades da v{infoVersao.versao}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              carregarDispositivos();
              carregarVersaoMaisRecente();
            }}
            disabled={carregando}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Atualizar Dados
          </Button>
        </Box>
      </Box>

      {/* Cards de Métricas */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
              <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'rgba(42, 81, 247, 0.1)', color: 'primary.main', display: 'flex' }}>
                <DevicesIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                  Total de Dispositivos
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.2 }}>
                  {carregando ? <Skeleton width={40} /> : totalDispositivos}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
              <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', display: 'flex' }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                  Agentes Online
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.2, color: '#2e7d32' }}>
                  {carregando ? <Skeleton width={40} /> : onlineCount}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
              <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: offlineCount > 0 ? 'rgba(211, 47, 47, 0.1)' : 'rgba(158, 158, 158, 0.1)', color: offlineCount > 0 ? '#d32f2f' : 'text.secondary', display: 'flex' }}>
                <ErrorOutlineIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                  Agentes Offline
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.2, color: offlineCount > 0 ? '#d32f2f' : 'text.primary' }}>
                  {carregando ? <Skeleton width={40} /> : offlineCount}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
              <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'rgba(2, 136, 209, 0.1)', color: '#0288d1', display: 'flex' }}>
                <SystemUpdateAltIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                    Última Versão
                  </Typography>
                  {infoVersao && (
                    <Chip
                      label={infoVersao.obrigatorio ? 'Obrigatória' : 'Disponível'}
                      size="small"
                      color={infoVersao.obrigatorio ? 'error' : 'info'}
                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {carregando ? <Skeleton width={60} /> : `v${versaoExibicao}`}
                  </Typography>
                  {infoVersao?.novidades && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setModalNovidadesAberto(true)}
                      sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600 }}
                    >
                      Ver novidades
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barra de Filtro e Busca */}
      <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Filtrar por nome, SO ou endereço IP..."
          value={filtroTexto}
          onChange={(e) => setFiltroTexto(e.target.value)}
          sx={{ width: { xs: '100%', sm: 360 }, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Exibindo {dispositivosFiltrados.length} de {dispositivos.length} dispositivo(s)
        </Typography>
      </Box>

      {/* Alerta de Erro */}
      {erro && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }} action={<Button color="inherit" size="small" onClick={carregarDispositivos}>Tentar Novamente</Button>}>
          {erro}
        </Alert>
      )}

      {/* Tabela de Dispositivos */}
      <TableContainer component={Paper} sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: 'none', overflow: 'hidden' }}>
        <Table sx={{ minWidth: 700 }} aria-label="tabela de dispositivos">
          <TableHead sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 1.8 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.8 }}>Dispositivo</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.8 }}>Sistema Operacional</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.8 }}>Versão Agente</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.8 }}>Endereços IP</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.8 }}>Armazenamento em Disco</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.8 }}>Último Heartbeat</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 1.8 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              Array.from(new Array(3)).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell><Skeleton width={80} height={28} /></TableCell>
                  <TableCell><Skeleton width={140} /><Skeleton width={90} height={14} /></TableCell>
                  <TableCell><Skeleton width={160} /></TableCell>
                  <TableCell><Skeleton width={70} /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                  <TableCell><Skeleton width={180} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell align="center"><Skeleton variant="circular" width={36} height={36} sx={{ mx: 'auto' }} /></TableCell>
                </TableRow>
              ))
            ) : dispositivosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                  <Box sx={{ color: 'text.secondary', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <DevicesIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                      Nenhum dispositivo localizado.
                    </Typography>
                    <Typography variant="body2" sx={{ maxWidth: 460 }}>
                      Certifique-se de que o serviço Backup Fácil esteja instalado e em execução no servidor para iniciar a sincronização automática.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              dispositivosFiltrados.map((disp) => {
                const isOnline = disp?.status === 'Online';
                const nomeDisp = disp?.nomeDispositivo || 'Servidor Principal';
                const soDisp = disp?.sistemaOperacional || 'Windows';
                const versaoDisp = disp?.versaoAgente || '1.2.6';
                const isServerType = nomeDisp.toLowerCase().includes('srv') || nomeDisp.toLowerCase().includes('server');

                const haAtualizacao = Boolean(
                  infoVersao?.versao && compararVersoes(infoVersao.versao, versaoDisp) > 0
                );

                return (
                  <TableRow
                    key={disp?.id || disp?.instalacaoID || Math.random()}
                    hover
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {/* Status */}
                    <TableCell>
                      <Chip
                        icon={<FiberManualRecordIcon sx={{ fontSize: '12px !important', color: isOnline ? '#2e7d32 !important' : '#9e9e9e !important' }} />}
                        label={isOnline ? 'Online' : 'Offline'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          bgcolor: isOnline ? 'rgba(46, 125, 50, 0.12)' : 'rgba(158, 158, 158, 0.12)',
                          color: isOnline ? '#2e7d32' : 'text.secondary',
                          border: `1px solid ${isOnline ? 'rgba(46, 125, 50, 0.3)' : 'rgba(158, 158, 158, 0.3)'}`,
                        }}
                      />
                    </TableCell>

                    {/* Dispositivo / Hostname */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        {isServerType ? (
                          <DnsIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        ) : (
                          <LaptopWindowsIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {nomeDisp}
                          </Typography>
                          {disp?.instalacaoID && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', display: 'block' }}>
                              ID: {String(disp.instalacaoID).substring(0, 12)}...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Sistema Operacional */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {soDisp}
                      </Typography>
                      {disp?.memoriaTotalMB && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                          <MemoryIcon sx={{ fontSize: 13 }} />
                          RAM: {Math.round(Number(disp.memoriaTotalMB) / 1024)} GB total ({disp.memoriaLivreMB ? `${Math.round(Number(disp.memoriaLivreMB) / 1024)} GB livre` : ''})
                        </Typography>
                      )}
                    </TableCell>

                    {/* Versão Agente */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`v${versaoDisp}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.75rem', borderColor: theme.palette.divider }}
                        />
                        {haAtualizacao ? (
                          <Tooltip title={`Nova versão disponível: v${infoVersao?.versao} (${infoVersao?.obrigatorio ? 'Obrigatória' : 'Opcional'})`}>
                            <Chip
                              label={`Nova: v${infoVersao?.versao}`}
                              size="small"
                              color={infoVersao?.obrigatorio ? 'error' : 'warning'}
                              sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Agente atualizado na versão mais recente">
                            <VerifiedIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    {/* Endereços IP */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', width: 45 }}>Local:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{disp?.ipLocal || '—'}</Typography>
                          {disp?.ipLocal && disp.ipLocal !== '—' && (
                            <IconButton size="small" sx={{ p: 0.2 }} onClick={() => copiarParaTransferencia(disp.ipLocal)}>
                              <ContentCopyIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            </IconButton>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', width: 45 }}>Externo:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{disp?.ipExterno || '—'}</Typography>
                          {disp?.ipExterno && disp.ipExterno !== '—' && (
                            <IconButton size="small" sx={{ p: 0.2 }} onClick={() => copiarParaTransferencia(disp.ipExterno)}>
                              <ContentCopyIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Armazenamento em Disco */}
                    <TableCell sx={{ minWidth: 200 }}>
                      {disp?.unidades && Array.isArray(disp.unidades) && disp.unidades.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {disp.unidades.slice(0, 3).map((u: IClienteUnidadeDisco) => {
                            const uso = Number(u.percentualUso || 0);
                            const tTotal = Number(u.tamanhoTotal || 0);
                            const tLivre = Number(u.tamanhoLivre || 0);
                            const tUsado = Math.max(0, tTotal - tLivre);
                            const corBarra = uso >= 90 ? '#d32f2f' : uso >= 75 ? '#ed6c02' : '#2e7d32';

                            return (
                              <Box key={u.id || u.unidade} sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <StorageIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                    {u.unidade}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {uso}% ({formatarBytesParaGB(tUsado)} / {formatarBytesParaGB(tTotal)})
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={uso}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                                    '& .MuiLinearProgress-bar': { bgcolor: corBarra, borderRadius: 3 },
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Nenhuma unidade informada
                        </Typography>
                      )}
                    </TableCell>

                    {/* Último Heartbeat */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatarTempoRelativo(disp?.ultimoHeartbeat)}
                      </Typography>
                      {disp?.ultimoHeartbeat && !isNaN(new Date(disp.ultimoHeartbeat).getTime()) && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                          {new Date(disp.ultimoHeartbeat).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Ações */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        {/* Botão de Atualização Remota */}
                        {haAtualizacao ? (
                          <Tooltip title={`Atualizar agente remotamente para a versão v${infoVersao?.versao}`}>
                            <span>
                              <IconButton
                                color="warning"
                                size="small"
                                onClick={() => handleAbrirModalAtualizacao(disp)}
                                sx={{
                                  border: `1px solid ${theme.palette.warning.main}`,
                                  borderRadius: '8px',
                                  bgcolor: 'rgba(237, 108, 2, 0.06)',
                                  '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.15)' },
                                }}
                              >
                                <SystemUpdateAltIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Atualizar agente para versão mais recente sob demanda">
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleAbrirModalAtualizacao(disp)}
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: '8px',
                                  '&:hover': { bgcolor: 'rgba(42, 81, 247, 0.08)' },
                                }}
                              >
                                <SystemUpdateAltIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Botão de Reinício */}
                        <Tooltip title="Reiniciar serviço do agente remotamente">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleAbrirModalReinicio(disp)}
                              sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '8px',
                                '&:hover': { bgcolor: 'rgba(42, 81, 247, 0.08)' },
                              }}
                            >
                              <RestartAltIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Novidades da Versão */}
      <Dialog
        open={modalNovidadesAberto}
        onClose={() => setModalNovidadesAberto(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CampaignIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            Novidades da Versão {infoVersao?.versao ? `v${infoVersao.versao}` : ''}
          </Box>
          {infoVersao && (
            <Chip
              label={infoVersao.obrigatorio ? 'Atualização Obrigatória' : 'Atualização Opcional'}
              color={infoVersao.obrigatorio ? 'error' : 'info'}
              size="small"
              sx={{ fontWeight: 700 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {infoVersao?.dataPublicacao && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              Publicada em: {new Date(infoVersao.dataPublicacao).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            O que mudou nesta versão:
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: '8px',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
              border: `1px solid ${theme.palette.divider}`,
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              lineHeight: 1.6,
              fontSize: '0.9rem',
              color: 'text.primary',
            }}
          >
            {infoVersao?.novidades || 'Melhorias de desempenho, estabilidade do mecanismo de backup e correções de segurança.'}
          </Box>

          {infoVersao?.obrigatorio && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2.5, borderRadius: '8px' }}>
              Esta versão é classificada como <strong>obrigatória</strong> e será aplicada automaticamente pelos agentes durante a sincronização periódica.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setModalNovidadesAberto(false)} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Atualização Remota do Agente */}
      <Dialog
        open={modalAtualizacaoAberto}
        onClose={handleFecharModalAtualizacao}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SystemUpdateAltIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          Atualização Remota do Agente
        </DialogTitle>
        <DialogContent>
          {resultadoAtualizacao ? (
            <Box sx={{ py: 2 }}>
              {resultadoAtualizacao.sucesso ? (
                <Alert severity="success" sx={{ borderRadius: '8px', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Atualização solicitada com sucesso!
                  </Typography>
                  <Typography variant="body2">
                    {resultadoAtualizacao.mensagem}
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="error" sx={{ borderRadius: '8px', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Falha na atualização remota
                  </Typography>
                  <Typography variant="body2">
                    {resultadoAtualizacao.mensagem}
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : atualizando ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Processando Atualização Remota
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420, mx: 'auto', mb: 2 }}>
                {statusAtualizacaoTexto}
              </Typography>
              <LinearProgress sx={{ maxWidth: 300, mx: 'auto', borderRadius: 2 }} />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <DialogContentText sx={{ color: 'text.primary', mb: 2 }}>
                Você está prestes a despachar a ordem de atualização remota para o dispositivo:
              </DialogContentText>

              <Box sx={{ p: 2, borderRadius: '8px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)', mb: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Dispositivo: {dispositivoSelecionado?.nomeDispositivo || 'Servidor Principal'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Versão atual instalada: <strong>v{dispositivoSelecionado?.versaoAgente || '1.2.6'}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', mt: 0.2, fontWeight: 600 }}>
                  Versão de destino: <strong>v{infoVersao?.versao || 'Mais recente'}</strong> {infoVersao?.obrigatorio ? '(Obrigatória)' : '(Opcional)'}
                </Typography>
              </Box>

              {infoVersao?.novidades && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                    Resumo das Novidades:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.3, fontSize: '0.85rem' }}>
                    {infoVersao.novidades}
                  </Typography>
                </Box>
              )}

              <DialogContentText sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                O cliente fará o download do pacote oficial em segundo plano, validará a integridade SHA-256 e o serviço será reiniciado de forma segura e transparente.
              </DialogContentText>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {resultadoAtualizacao ? (
            <Button
              onClick={handleFecharModalAtualizacao}
              variant="contained"
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
            >
              Concluir
            </Button>
          ) : atualizando ? (
            <Button
              onClick={handleFecharModalAtualizacao}
              variant="text"
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Acompanhar em Segundo Plano
            </Button>
          ) : (
            <>
              <Button onClick={handleFecharModalAtualizacao} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmarAtualizacao}
                variant="contained"
                color="primary"
                startIcon={<SystemUpdateAltIcon />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
              >
                Atualizar Agente Agora
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação para Reiniciar Agente */}
      <Dialog
        open={modalReinicioAberto}
        onClose={handleFecharModalReinicio}
        aria-labelledby="dialog-reiniciar-titulo"
        aria-describedby="dialog-reiniciar-descricao"
        PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
      >
        <DialogTitle id="dialog-reiniciar-titulo" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RestartAltIcon sx={{ color: 'primary.main' }} />
          Confirmar Reinício do Agente
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="dialog-reiniciar-descricao" sx={{ color: 'text.primary', mb: 2 }}>
            Você está prestes a enviar uma solicitação para reiniciar o serviço do Backup Fácil no seguinte dispositivo:
          </DialogContentText>
          <Box sx={{ p: 2, borderRadius: '8px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)', mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Dispositivo: {dispositivoSelecionado?.nomeDispositivo || 'Servidor Principal'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Sistema Operacional: {dispositivoSelecionado?.sistemaOperacional || 'Windows'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              IP Local: {dispositivoSelecionado?.ipLocal || '—'}
            </Typography>
          </Box>
          <DialogContentText sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            O serviço será reiniciado em segundo plano e reconectará automaticamente em alguns segundos. Tarefas de backup em andamento podem ser pausadas momentaneamente.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleFecharModalReinicio} disabled={reiniciando} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarReinicio}
            variant="contained"
            color="primary"
            disabled={reiniciando}
            startIcon={reiniciando ? <CircularProgress size={18} color="inherit" /> : <RestartAltIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
          >
            {reiniciando ? 'Despachando...' : 'Confirmar Reinício'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbarAberto}
        autoHideDuration={4000}
        onClose={() => setSnackbarAberto(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarAberto(false)} severity={snackbarSeveridade} sx={{ width: '100%', borderRadius: '8px', fontWeight: 600 }}>
          {snackbarMensagem}
        </Alert>
      </Snackbar>
    </Box>
  );
}
